import ky from "ky";
import queryString from "query-string";

import { jwt } from "../utils/index.js";

const { REACT_APP_MAIN_SERVER_URL } = process.env;

const rootApi = ky.extend({
	timeout: false,
	prefixUrl: `${REACT_APP_MAIN_SERVER_URL}/api`,
	retry: {
		statusCodes: [401, 408, 413, 429, 502, 503, 504],
		limit: 2,
		methods: ["get", "post", "put", "head", "delete", "options", "trace"],
	},
	hooks: {
		beforeRequest: [({ headers }) => {
			headers.set("x-access-token", jwt.getToken());
		}],
		beforeRetry: [
			async ({ request: { method }, error }) => {
				if (error?.response?.status === 401) {
					const res = await rootApi.extend({ throwHttpErrors: false, retry: 0 }).get("api/refresh");
					if (res.status === 401) {
						jwt.destroyToken();
						window.location.href = "/";
					} else {
						const { token } = await res.json();
						jwt.setToken(token);
					}
				} else if (method === "POST") {
					throw error;
				}
			},
		],
		afterResponse: [
			(_, __, res) => {
				const { status } = res;
				if (status === 500) {
					return new Response(JSON.stringify({ success: false }), { status: 200 });
				}

				if (status === 404) {
					window.location.href = "/";
				}

				return res;
			},
		],
	},
});

const api = {
	get: (path, searchParams) => rootApi.get(path, { searchParams: queryString.stringify(searchParams) }).json(),
	post: (path, json) => rootApi.post(path, { json }).json(),
	put: (path, json) => rootApi.put(path, { json }).json(),
	patch: (path, json) => rootApi.patch(path, { json }).json(),
	delete: (path, json) => rootApi.delete(path, { json }).json(),
	download: (path) => rootApi.get(path).blob(),
};

export default api;

export const authenticate = (emailOrUsername, password) => api.post("user-system/authenticate", { emailOrUsername, password });
export const authenticateGoogle = (token) => api.post("user-system/authenticateGoogle", { token });
export const forgotPassword = (username) => api.post("user-system/forgotPassword", { username });
export const resetPassword = (password, token) => api.post("user-system/resetPassword", { password, token });
export const signUp = (username, email, password) => api.post("user-system/createUser", { username, email, password });
export const invitedSignUp = (username, fullName, password, email, token) => api.post("user-system/createUserInvited", { username, fullName, password, email, token });
export const uploadFile = (body) => rootApi.post("file/", { body }).json();
export const reUploadFile = (body) => rootApi.put("file/", { body }).json();
export const deleteFile = (info) => api.post("file/delete/", info);
export const inviteUser = (email, role, orgId, fullName, kentroId = null) => api.post("user-system", { email, role, orgId: kentroId ? null : orgId, kentroId: kentroId || undefined, fullName });
export const getKentros = () => api.get("kentro");
export const getKentroOrganizations = () => api.get("kentro/organizations");
export const getOwnersByOrganization = (organizationId) => api.get(`owner/by-organization/${organizationId}`);
export const removeUser = (id) => api.post("user/delete", { id });
export const getUsersData = () => api.get("user");
export const getInvitedUsers = () => api.get("user/invited");
export const deleteInvitedUser = (email) => api.delete("user/invited", { email });
export const getUserRole = () => api.get("user/role");
export const getUserOrg = () => api.get("user/org");
export const resendInvitation = (email) => api.post("user-system/resend", { email });
// Owners
export const createOwner = (values) => api.post("owner", { values });
export const addExternalSaleAnimalsToOwner = (ownerId, animalIds, declarationAccepted) => api.post(`owner/${ownerId}/external-sale-animals`, { animalIds, declarationAccepted });
export const getExternalSaleAnimalsForOwner = (ownerId) => api.get(`owner/${ownerId}/external-sale-animals`);
export const getOwners = () => api.get("owner");
export const getOwner = (id) => api.get(`owner/${id}`);
export const updateOwner = (id, values) => api.put(`owner/${id}`, { values });
export const deleteOwner = (ownerId) => api.post("owner/delete", { ownerId })
export const changeCode = (ownerId, newCode) => api.post("owner/change/code", { ownerId, newCode });
export const getArchivedOwners = () => api.get("owner/archived/list");
export const archiveOwners = (ownerIds) => api.put("owner/archive", { ownerIds });
export const unarchiveOwners = (ownerIds) => api.put("owner/unarchive", { ownerIds });
// Owner display code proxy mappings (admin only)
export const getOwnerDisplayCodes = () => api.get("owner-display-codes");
export const createOwnerDisplayCode = (ownerId, organizationId, displayCode) => api.post("owner-display-codes", { ownerId, organizationId, displayCode });
export const updateOwnerDisplayCode = (id, displayCode) => api.put(`owner-display-codes/${id}`, { displayCode });
export const deleteOwnerDisplayCode = (id) => api.delete(`owner-display-codes/${id}`);
// Inspectors
export const getInspectors = () => api.get("inspector");
export const createInspector = (values) => api.post("inspector", { values });
export const getInspector = (id) => api.get(`inspector/${id}`);
export const updateInspector = (id, values) => api.put(`inspector/${id}`, { values });
export const deleteInspector = (inspectorId) => api.post("inspector/delete", { inspectorId });
// Animals
export const createAnimal = (values) => api.post("animal", { values });
export const claimExternalSaleAnimal = (animalId, ownerId, declarationAccepted) => api.post("animal/external-sale-claim", { animalId, ownerId, declarationAccepted });
export const checkIfAnimalExists = (registrationCode, ownerId) => api.get(`animal/checkExistance/${registrationCode}/${ownerId}`);
export const getAnimal = (registrationCode, ownerId) => api.get(`animal/${registrationCode}/${ownerId}`);
export const getAnimalByFlockCode = (flockCode, ownerId) => api.get(`animal/flockCode/${flockCode}/${ownerId}`);
export const updateAnimal = (animalId, ownerId, values) => api.put(`animal/${animalId}/${ownerId}`, { values });
export const deleteAnimal = (animalId) => api.post("animal/delete", { animalId });
export const getAnimals = (ownerId) => api.get(`animal/${ownerId}`);
export const getAnimalsByGender = (ownerId, sex) => api.get(`animal/${ownerId}?sex=${sex}`);
export const getAnimalsByStatus = (ownerId, status) => api.get(`animal/${ownerId}?status=${status}`);
export const getAnimalsByGenderAndStatus = (ownerId, sex, status) => api.get(`animal/${ownerId}?status=${status}&sex=${sex}`);
export const getPedigree = (ownerId, registrationCode) => api.get(`animal/pedigree/${ownerId}/${registrationCode}`);
export const addUdder = (values) => api.post("udder", { values });
export const getUdderAnimals = (ownerId) => api.get(`udder/animals/${ownerId}`);
export const getUdders = (animalId) => api.get(`udder/${animalId}`);
export const getUdderById = (udderId) => api.get(`udder/udder/${udderId}`);
export const updateUdder = (udderId, values) => api.put(`udder/${udderId}`, { values });
export const deleteUdder = (udderId) => api.delete(`udder/${udderId}`);
export const getAnimalSupervisionData = (animalId) => api.get(`animal/supervision/data/${animalId}`);
export const getAnimalSupervisionMilkRecordings = (animalId, lambingId) => api.get(
	`animal/supervision/milk-recordings/${animalId}/${lambingId}`,
);
export const getCurrentAnimalOwner = (registrationCode) => api.get(`animal/current/owner/${registrationCode}`);
export const changeAnimalRegistrationCode = (animalId, newRegistrationCode) => api.post(`animal/change/registrationCode`, { animalId, newRegistrationCode });
export const groupCulling = (ownerId, cullingData) => api.post(`animal/groupCulling/${ownerId}`, { cullingData });
export const resolveGroupCullingAnimals = (ownerId, codes) => api.post(`animal/groupCulling/resolve/${ownerId}`, codes);
export const cullSelectedAnimals = (ownerId, values) => api.post(`animal/groupCulling/selected/${ownerId}`, values);

// Matings
export const getMatingFatherData = (damId) => api.get(`mating/sireData/${damId}`);
export const getRelevantMatingSire = (damId, lambingDate) => api.get(
    `mating/relevant-sire/${damId}`,
    { lambingDate },
);
export const getEligibleGroupMatingDams = (damOwnerId, matingDate1, matingDate2) => api.post(
    "mating/eligible-group-dams",
    { damOwnerId, matingDate1, matingDate2 },
);
export const createMating = (values, confirmScheduleWarnings = false) => api.post("mating", {
    values,
    confirmScheduleWarnings,
});
export const getMatings = (owner, sex) => api.get(`mating/${owner}/${sex}`);
export const getRelationships = (maleRegistrationCode, maleOwnerId, femaleOwnerId) => api.post("mating/relationship", { maleRegistrationCode, maleOwnerId, femaleOwnerId });
export const getYearsMatings = (ownerId, year) => api.get(`mating/all/${ownerId}/${year}`);
export const deleteMating = (id) => api.delete(`mating/${id}`);
// Lambings
export const createLambing = (lambingAndLambData, confirmMatingWarnings = false) => api.post('lambing', {
	lambingAndLambData,
	confirmMatingWarnings,
});
export const getLambings = (ownerId, animalId) => api.get(`lambing/many/${ownerId}/${animalId}`);
export const getLambs = (lambingId) => api.get(`lambing/lambs/${lambingId}`);
export const updateLambing = (lambingId, lambingAndLambData) => api.put(`lambing/${lambingId}`, { lambingAndLambData });
export const updateLambingSire = (lambingId, sireCode) => api.put(`lambing/${lambingId}/sire`, { sireCode });
export const deleteLambing = (lambingId) => api.delete(`lambing/${lambingId}`);
export const endLambingPeriod = (lambingId, weaningDate) => api.post(`lambing/end/lambs/${lambingId}`, { weaningDate });
export const endLambingPeriodByDateRange = (ownerId, values) => api.post(`lambing/end/lambing-period/${ownerId}`, values);
export const countLambsToRelease = (ownerId, values) => api.post(`lambing/end/lambing-period/count/${ownerId}`, values);
export const getLambWeights = (lambSlug, ownerId) => api.get(`lambing/lambs-weights/${lambSlug}/${ownerId}`);
export const addLambWeight = (lambSlug, ownerId, weightObj) => api.post(`lambing/lambs-weight/${lambSlug}/${ownerId}`, { weightObj });
export const updateLambWeight = (lambSlug, ownerId, values) => api.put(`lambing/lamb-weight/${lambSlug}/${ownerId}`, { values });
export const deleteLambWeight = (lambSlug, ownerId, values) => api.delete(`lambing/lamb-weight/${lambSlug}/${ownerId}`, { values });
export const getLambingData = (ownerId, firstYear, secondYear) => api.get(`lambing/data/${ownerId}/${firstYear}/${secondYear}`);
export const getLastAnimalLambing = (ownerId, animalId) => api.get(`lambing/last/${ownerId}/${animalId}`);
export const getLambingDateByDateRange = (ownerId, values) => api.post(`lambing/date-range/${ownerId}`, { values });
export const getLambsByDateRange = (ownerId, values) => api.post(`lambing/date-range/lambs/${ownerId}`, { values });
export const getLambingsByDateRange = (ownerId, values) => api.post(`lambing/date-range/lambings/${ownerId}`, { values });
export const addNewLambInFlock = (ownerId, values) => api.post(`lambing/update-lamb/${ownerId}`, { values });
export const getYearsLambings = (ownerId, year) => api.get(`lambing/all/${ownerId}/${year}`);
export const updateLambStatus = (lambId, ownerId, values) => api.put(`lambing/update-lamb-status/${lambId}/${ownerId}`, { values });
export const getLambCodeHistory = (ownerId, lambSlug) => api.get(`lambing/code-history/${ownerId}/${lambSlug}`);
// Milk recordings
export const createMilkRecording = (values) => api.post("milkrecording", { values });
export const getMilkRecordings = (ownerId) => api.get(`milkrecording/${ownerId}`);
export const getMilkRecording = (ownerId, recordingId) => api.get(`milkrecording/${ownerId}/${recordingId}`);
export const updateMilkRecording = (
    recordingId,
    values,
    confirmDateChange = false,
    confirmMilkingAddition = false,
    confirmRecordingChange = false,
    changeVersion = null,
) => api.put(
    `milkrecording/${recordingId}`,
    { values, confirmDateChange, confirmMilkingAddition, confirmRecordingChange, changeVersion },
);
export const getMilkRecordingCompleteness = (recordingId) => api.get(`milkrecording/completeness/${recordingId}`);
export const transferMilkRecordingMeasurements = (recordingId, values = {}) => api.post(`milkrecording/measurements/transfer/${recordingId}`, values);
export const getAnimalsForMilkRecording = (recordingId) => api.get(`milkrecording/animals/milk-recordings/${recordingId}`);
export const checkIfAnimalIsEligible = (recordingId, registrationCode) => api.get(`milkrecording/animal/check/${recordingId}/${registrationCode}`);
export const createAnimalMilkRecording = (values) => api.post("milkrecording/animal/milk-recording", { values });
export const getAnimalMilkRecordingsAndAnimals = (recordingId) => api.get(`milkrecording/recorded/animals/${recordingId}`);
export const updateAnimalMilkRecording = (animalMilkRecordingId, values) => api.put(`milkrecording/animal/milk-recording/${animalMilkRecordingId}`, { values });
export const deleteAnimalMilkRecording = (animalMilkRecordingId) => api.delete(`milkrecording/animal/${animalMilkRecordingId}`);
export const getMilkCalculation = (milkRecordingId) => api.get(`milkrecording/calculation/metrics/${milkRecordingId}`);
export const deleteMilkRecording = (milkRecordingId) => api.delete(`milkrecording/${milkRecordingId}`);
export const getMetrics = (milkRecordingId) => api.get(`milkrecording/metrics/animals/${milkRecordingId}`);
export const addMetrics = (animalMilkRecordingId, values) => api.put(`milkrecording/metrics/${animalMilkRecordingId}`, { values });
export const calculateTotalMilk = (values) => api.post("milkrecording/total/animals/milk-recordings", { values });
export const getAnimalsWithActiveMlkRcrd = (values) => api.post("milkrecording/animals/active-milking-period", { values });
export const endMilkingPeriod = (values) => api.post("milkrecording/period/end", { values });
export const cancelEndMilkingPeriod = (values) => api.post("milkrecording/period/cancel-end", { values });
export const getTablesForAnimalsData = (ownerId, milkRecordingId) => api.get(`milkrecording/tables/info/${ownerId}/${milkRecordingId}`);
// Orders
export const createAnimalsForSale = (values) => api.post("orders/animals", { values });
export const getAvailableAnimalsSale = (ownerId, type) => api.get(`orders/animals/${ownerId}/${type}`);
export const createOrder = (values) => api.post("orders", { values });
export const checkLambSlug = (ownerId, lambSlugs) => api.post(`orders/lamb/check/${ownerId}`, { lambSlugs });
export const getOrdersHistory = () => api.get("orders/history");
export const getOrder = (orderNumber) => api.get(`orders/${orderNumber}`);
export const executeOrder = (values) => api.post("orders/execute", { values });
export const getAvailableAnimalsAllTypes = (ownerId) => api.get(`orders/animals/${ownerId}`);
export const updateAvailableAnimal = (values, ownerId, code) => api.put(`orders/animal/${ownerId}/${code}`, { values });
export const deleteAvailableAnimal = (ownerId, code) => api.delete(`orders/animal/${ownerId}/${code}`);
export const getOrderedAnimals = (year, type) => api.get(`orders/animals/ordered/${year}/${type}`);
// Transports
export const createTransport = (values) => api.post("transports", { values });
export const resolveTransportAnimals = (ownerId, codes) => api.post(`transports/resolve/${ownerId}`, codes);
export const createBatchTransport = (values) => api.post("transports/batch", { values });
export const getTransportsHistory = (ownerId, animalId) => api.get(`transports/history/${ownerId}/${animalId}`);
// Owner Transport
export const transportOwner = (values) => api.post("ownerTransport", { values });
// Organization
export const getOrganization = () => api.get("organization/user");
export const getOrganizations = () => api.get("organization");
export const updateOrganization = (id, values) => api.put(`organization/${id}`, { values });
// Info
export const getMaleFamilyTree = (animalId) => api.get(`info/male/animal/${animalId}/tree`);
export const getAnimalRelatives = (animalId) => api.get(`info/relatives/${animalId}`);
export const getMonthlyMilkData = (ownerId, year, month) => api.get(`info/monthly/milk-production/${ownerId}/${year}/${month}`);
export const getAnimalsRegistrationData = (animalId) => api.get(`info/animal/registration/${animalId}`);
export const getAllAnimalsRegistrationData = (ownerId) => api.get(`info/owner/registration/${ownerId}`);
export const getAnimalRegistraitonLogs = () => api.get("info/animal/registration-logs");
export const getOwnerPopulation = (ownerId, year) => api.get(`info/owner/population/${ownerId}/${year}`);
export const getOwnerPopulationDetailed = (ownerId, startYear) => api.get(`info/owner/population/detailed/${ownerId}/${startYear}`);
export const getLastUpdateDate = (ownerId) => api.get(`info/last/date/owner/updated/${ownerId}`);
export const createInventory = (values) => api.post(`info/owner/inventory`, { values });
export const getInventories = (ownerId) => api.get(`info/owner/inventories/${ownerId}`);
export const deleteInventory = (inventoryId) => api.delete(`info/owner/inventory/${inventoryId}`);
export const getYearMilking = (ownerId, category) => api.get(`info/year/milking/${ownerId}/${category}`);
// Scrapie
export const getAvailableScrapieAnimals = (ownerId, sampleDate) => api.post("scrapie/available/animals", { ownerId, sampleDate });
export const checkAnimalExistence = (ownerId, flockCode) => api.get(`scrapie/check/existence/${ownerId}/${flockCode}`);
export const createScrapie = (ownerId, animalId, sampleCode, sampleDate, resultDate, sampleState, result1, result2) => api.post("scrapie", { ownerId, animalId, sampleCode, sampleDate, resultDate, sampleState, result1, result2 });
export const getScrapies = (ownerId) => api.get(`scrapie/${ownerId}`);
export const updateScrapie = (scrapieId, ownerId, animalId, sampleCode, sampleDate, resultDate, sampleState, result1, result2) => api.put(`scrapie/${scrapieId}`, { ownerId, animalId, sampleCode, sampleDate, resultDate, sampleState, result1, result2 });
export const deleteScrapie = (scrapieId) => api.delete(`scrapie/${scrapieId}`);
export const getScrapieWithStatus = (ownerId, status) => api.get(`scrapie/${ownerId}/${status}`);
export const getAvailableScrapieLambs = (ownerId, startLambingDate, endLambingDate, sampleDate) => api.post("scrapie/available/lambs", { ownerId, startLambingDate, endLambingDate, sampleDate });
export const createLambScrapie = (ownerId, lambSlug, sampleCode, sampleDate, resultDate, sampleState, result1, result2) => api.post("scrapie/lamb", { ownerId, lambSlug, sampleCode, sampleDate, resultDate, sampleState, result1, result2 });
export const getLambScrapies = (ownerId, startLambingDate, endLambingDate) => api.post(`scrapie/lamb/${ownerId}`, { startLambingDate, endLambingDate });
export const updateLambScrapie = (scrapieId, ownerId, animalId, sampleCode, sampleDate, resultDate, sampleState, result1, result2) => api.put(`scrapie/lamb/${scrapieId}`, { ownerId, animalId, sampleCode, sampleDate, resultDate, sampleState, result1, result2 });
export const deleteLambScrapie = (scrapieId) => api.delete(`scrapie/lamb/${scrapieId}`);
// Admin actions
export const triggerGeneticEvaluation = () => api.post("admin-actions/trigger-genetic-evaluation");
export const triggerRelationshipsCalculation = () => api.post("admin-actions/trigger-relationships-calculation");
export const getActionsStatuses = () => api.get("admin-actions/status");
export const downloadActionResults = (actionId) => api.download(`admin-actions/${actionId}/download`);
// Report routes
export const getAllProblemReports = () => api.get("problem-reports");
export const getProblemReport = (id) => api.get(`problem-reports/${id}`);
export const createProblemReport = (title) => api.post("problem-reports", { title });
export const updateProblemReport = (id, data) => api.put(`problem-reports/${id}`, { data });
export const deleteProblemReport = (id) => api.delete(`problem-reports/${id}`);
